import { useEffect, useRef, useState } from 'react'
import { Link, useBlocker } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ApiError, type TShow, type TShowSeries } from '@schemas'
import { useSeriesContext } from '@/layouts/SeriesLayout'
import { useCurrentShow } from '@/context/current-show'
import {
  reportActorKey,
  useCurrentUser,
  useOrganizations,
  useSeriesEditions,
  useShowSeries,
  useShowSeriesDetail,
  useUpdateShow,
  useUpdateShowSeries,
} from '@/api/hooks'
import { liveApiClient } from '@/api/live-query'
import { apiClient } from '@/lib/apiClient'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'

function message(error: unknown) {
  return error instanceof ApiError
    ? String(error.payload?.message ?? error.message)
    : 'Unable to save. Please try again.'
}
function denied(error: unknown) {
  return error instanceof ApiError && [401, 403, 404].includes(error.status)
}
type MembershipAction = { show: TShow; destination: string | null }

export default function SeriesSettingsPage() {
  const series = useSeriesContext()
  const user = useCurrentUser()
  const organizations = useOrganizations()
  const editions = useSeriesEditions(series.id)
  const destinations = useShowSeries(series.organizationId)
  const candidates = useQuery({
    queryKey: ['show-series', 'manageable-shows', series.id, ...reportActorKey(user.data)],
    queryFn: async () =>
      (await liveApiClient.listShows({ orgId: series.organizationId, when: 'all' })).shows,
    enabled: Boolean(user.data),
    retry: false,
    refetchOnWindowFocus: true,
  })
  const updateShow = useUpdateShow()
  const rename = useUpdateShowSeries()
  const detail = useShowSeriesDetail(series.id)
  const { currentShow, setCurrentShow } = useCurrentShow()
  const [name, setName] = useState(series.name)
  const [savedName, setSavedName] = useState(series.name)
  const [isChampionship, setIsChampionship] = useState(series.isChampionship)
  const [savedChampionship, setSavedChampionship] = useState(series.isChampionship)
  const [revision, setRevision] = useState(series.metadataRevision)
  const [renameConflict, setRenameConflict] = useState(false)
  const [freshName, setFreshName] = useState<TShowSeries | null>(null)
  const [action, setAction] = useState<MembershipAction | null>(null)
  const [freshShow, setFreshShow] = useState<TShow | null>(null)
  const [membershipConflict, setMembershipConflict] = useState(false)
  const [reloading, setReloading] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [candidateId, setCandidateId] = useState('')
  const alive = useRef(true)
  useEffect(() => {
    alive.current = true
    return () => {
      alive.current = false
    }
  }, [])
  const busy = updateShow.isPending || rename.isPending || reloading
  const metadataDirty = name !== savedName || isChampionship !== savedChampionship
  const dirty = metadataDirty || Boolean(action)
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      (dirty || busy) && currentLocation.pathname !== nextLocation.pathname
  )
  useEffect(() => {
    if (!dirty && !busy) return
    const beforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', beforeUnload)
    return () => window.removeEventListener('beforeunload', beforeUnload)
  }, [dirty, busy])
  useEffect(() => {
    // Our own save refetches the series before its handler adopts the new revision.
    if (series.metadataRevision === revision || rename.isPending) return
    if (!metadataDirty && !renameConflict) {
      setName(series.name)
      setSavedName(series.name)
      setIsChampionship(series.isChampionship)
      setSavedChampionship(series.isChampionship)
      setRevision(series.metadataRevision)
    } else setRenameConflict(true)
  }, [
    series.metadataRevision,
    series.name,
    series.isChampionship,
    revision,
    metadataDirty,
    renameConflict,
    rename.isPending,
  ])

  const readableCandidates = candidates.isError ? [] : (candidates.data ?? [])
  const manageable = readableCandidates.filter(
    show => show.orgId === series.organizationId && show.seriesMembership?.canEdit
  )
  const available = manageable.filter(show => show.seriesMembership?.series?.id !== series.id)
  const allowedDestinations = destinations.isError
    ? []
    : (destinations.data?.pages.flatMap(page => page.series) ?? [])
  const actionStillEditable =
    action && !candidates.isError && manageable.some(show => show.id === action.show.id)
  useEffect(() => {
    if (
      action &&
      (denied(candidates.error) ||
        (!candidates.isFetching && candidates.data && !actionStillEditable))
    ) {
      setAction(null)
      setFreshShow(null)
      setMembershipConflict(false)
      setError(
        'Membership editing is no longer available for that show. Reload the available shows before trying again.'
      )
    }
  }, [action, actionStillEditable, candidates.error, candidates.isFetching, candidates.data])

  async function reloadName() {
    setReloading(true)
    setFreshName(null)
    try {
      const result = await detail.refetch({ throwOnError: true })
      if (alive.current && result.data) setFreshName(result.data.series)
    } catch {
      if (alive.current)
        setError('Unable to reload the saved series name. Retry loading it before saving.')
    } finally {
      if (alive.current) setReloading(false)
    }
  }
  async function saveName() {
    const trimmed = name.trim()
    if (!trimmed || trimmed.length > 200 || renameConflict || busy) return
    setError('')
    setNotice('')
    try {
      const result = await rename.mutateAsync({
        seriesId: series.id,
        payload: { name: trimmed, isChampionship, expectedMetadataRevision: revision },
      })
      if (!alive.current) return
      setName(result.series.name)
      setSavedName(result.series.name)
      setIsChampionship(result.series.isChampionship)
      setSavedChampionship(result.series.isChampionship)
      setRevision(result.series.metadataRevision)
      setRenameConflict(false)
      setNotice('Series details saved.')
    } catch (failure) {
      if (!alive.current) return
      setError(message(failure))
      if (failure instanceof ApiError && failure.payload?.code === 'SERIES_METADATA_STALE') {
        setRenameConflict(true)
        await reloadName()
      }
    }
  }
  function begin(show: TShow, destination: string | null) {
    setAction({ show, destination })
    setFreshShow(null)
    setMembershipConflict(false)
    setError('')
    setNotice('')
  }
  async function reloadMembership() {
    if (!action) return
    setReloading(true)
    setFreshShow(null)
    try {
      const result = await apiClient.lookupShow({ showId: action.show.id! })
      if (alive.current) setFreshShow(result.show)
    } catch (failure) {
      if (alive.current) setError(message(failure))
    } finally {
      if (alive.current) setReloading(false)
    }
  }
  async function saveMembership() {
    if (
      !action ||
      !actionStillEditable ||
      busy ||
      membershipConflict ||
      !action.show.seriesMembership?.canEdit
    )
      return
    if (action.destination && !allowedDestinations.some(item => item.id === action.destination))
      return
    setError('')
    setNotice('')
    try {
      const updated = await updateShow.mutateAsync({
        showId: action.show.id!,
        payload: {
          seriesId: action.destination,
          expectedMembershipRevision: action.show.seriesMembership.revision,
        },
      })
      if (!alive.current) return
      if (currentShow?.id === updated.id) setCurrentShow(updated)
      setAction(null)
      setCandidateId('')
      setNotice('Series membership saved.')
    } catch (failure) {
      if (!alive.current) return
      setError(message(failure))
      if (failure instanceof ApiError && failure.payload?.code === 'SERIES_MEMBERSHIP_STALE') {
        setMembershipConflict(true)
        await reloadMembership()
      }
    }
  }
  const organization = organizations.data?.find(item => item.id === series.organizationId)
  const destinationName = action?.destination
    ? allowedDestinations.find(item => item.id === action.destination)?.name
    : 'None'
  return (
    <div className="space-y-8">
      <section className="space-y-3" aria-label="Series details">
        <h2 className="text-xl font-semibold">Series settings</h2>
        <p>
          Organization: {organization?.name ?? (organizations.isError ? 'Unavailable' : 'Loading…')}
        </p>
        <p>Series URL name: {series.slug}</p>
        <p className="text-sm text-muted-foreground">
          The organization and URL name cannot be changed.
        </p>
        {user.data?.isAdmin ? (
          <form
            className="space-y-3"
            onSubmit={event => {
              event.preventDefault()
              void saveName()
            }}
          >
            <label className="block">
              Series name
              <Input
                value={name}
                onChange={event => setName(event.target.value)}
                maxLength={200}
                required
                disabled={busy}
              />
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={isChampionship}
                onChange={event => setIsChampionship(event.target.checked)}
                disabled={busy}
              />
              Championship series
            </label>
            <p className="text-sm text-muted-foreground">
              Each edition gets championship statistics after organizers review and release its
              results. Existing public releases change only after a reviewed replacement.
            </p>
            {renameConflict && (
              <div role="alert" className="space-y-2">
                {freshName && freshName.metadataRevision === series.metadataRevision ? (
                  <>
                    <p>
                      Saved series name: {freshName.name}. Championship series:{' '}
                      {freshName.isChampionship ? 'Yes' : 'No'}. Review these details before saving
                      your draft.
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      disabled={busy}
                      onClick={() => {
                        // Only an edited field is a draft; the other follows the saved value.
                        if (name === savedName) setName(freshName.name)
                        if (isChampionship === savedChampionship)
                          setIsChampionship(freshName.isChampionship)
                        setRevision(freshName.metadataRevision)
                        setSavedName(freshName.name)
                        setSavedChampionship(freshName.isChampionship)
                        setRenameConflict(false)
                        setFreshName(null)
                        setError('')
                      }}
                    >
                      I have reviewed the saved name
                    </Button>
                  </>
                ) : (
                  <>
                    <p>The series details changed. Reload them before saving your draft.</p>
                    <Button
                      type="button"
                      variant="outline"
                      disabled={busy}
                      onClick={() => void reloadName()}
                    >
                      Reload saved name
                    </Button>
                  </>
                )}
              </div>
            )}
            <Button
              disabled={
                busy ||
                renameConflict ||
                !name.trim() ||
                (name.trim() === savedName && isChampionship === savedChampionship)
              }
            >
              Save series details
            </Button>
          </form>
        ) : (
          <div className="space-y-2">
            <p>Championship series: {series.isChampionship ? 'Yes' : 'No'}</p>
            <p>Only administrators can edit series details.</p>
          </div>
        )}
      </section>
      {error && <p role="alert">{error}</p>}
      {notice && <p role="status">{notice}</p>}
      <section className="space-y-4" aria-label="Series editions">
        <h2 className="text-xl font-semibold">Member shows</h2>
        {editions.isError ? (
          <div role="alert">
            <p>Unable to load member shows.</p>
            <Button onClick={() => void editions.refetch()}>Retry member shows</Button>
          </div>
        ) : editions.isPending ? (
          <p role="status">Loading member shows…</p>
        ) : (
          <>
            {!editions.data.pages.some(page => page.editions.length) && (
              <p>No readable editions are in this series yet.</p>
            )}
            <ul className="space-y-3">
              {editions.data.pages
                .flatMap(page => page.editions)
                .map(edition => {
                  const show = manageable.find(
                    item =>
                      item.id === edition.showId && item.seriesMembership?.series?.id === series.id
                  )
                  return (
                    <li key={edition.showId} className="rounded border p-3 space-y-2">
                      <p>
                        {edition.year} · {edition.name} · {edition.status}
                      </p>
                      <div className="flex flex-wrap gap-3">
                        <Link className="underline" to={`/${edition.slug}/setup`}>
                          Show Setup<span className="sr-only"> for {edition.name}</span>
                        </Link>
                        {edition.canEdit && show ? (
                          <>
                            <Button
                              variant="outline"
                              disabled={busy || Boolean(action)}
                              onClick={() => begin(show, null)}
                            >
                              Remove {edition.name}
                            </Button>
                            <Button
                              variant="outline"
                              disabled={busy || Boolean(action)}
                              onClick={() => begin(show, series.id)}
                            >
                              Move {edition.name}
                            </Button>
                          </>
                        ) : (
                          <span className="text-sm text-muted-foreground">
                            Read-only membership
                          </span>
                        )}
                      </div>
                    </li>
                  )
                })}
            </ul>
            {editions.hasNextPage && (
              <Button
                disabled={editions.isFetchingNextPage}
                onClick={() => void editions.fetchNextPage()}
              >
                Load more member shows
              </Button>
            )}
          </>
        )}
        {candidates.isError ? (
          <div role="alert">
            <p>Unable to load shows you can manage.</p>
            <Button onClick={() => void candidates.refetch()}>Retry available shows</Button>
          </div>
        ) : (
          <div className="space-y-2">
            <label className="block">
              Add or move a show into this series
              <select
                className="block w-full rounded border p-2"
                value={candidateId}
                onChange={event => setCandidateId(event.target.value)}
                disabled={busy || Boolean(action) || candidates.isPending}
              >
                <option value="">Choose a show you can manage</option>
                {available.map(show => (
                  <option key={show.id} value={show.id}>
                    {show.name} · {new Date(show.startDate).getUTCFullYear()} ·{' '}
                    {show.seriesMembership?.series?.name ?? 'No series'}
                  </option>
                ))}
              </select>
            </label>
            <Button
              disabled={busy || Boolean(action) || !available.some(show => show.id === candidateId)}
              onClick={() => {
                const show = available.find(show => show.id === candidateId)
                if (show) begin(show, series.id)
              }}
            >
              Review membership change
            </Button>
          </div>
        )}
      </section>
      <Dialog
        open={Boolean(action)}
        onOpenChange={open => {
          if (!open && !busy) {
            setAction(null)
            setMembershipConflict(false)
            setFreshShow(null)
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm series membership</DialogTitle>
            <DialogDescription>
              Review this change before saving. Only this show's series membership will change.
            </DialogDescription>
          </DialogHeader>
          {action && (
            <div className="space-y-3">
              <p>
                {action.show.name} · {new Date(action.show.startDate).getUTCFullYear()}
              </p>
              <p>From: {action.show.seriesMembership?.series?.name ?? 'None'}</p>
              {action.destination !== null ? (
                <label className="block">
                  Destination series
                  <select
                    className="block w-full rounded border p-2"
                    value={action.destination}
                    disabled={busy}
                    onChange={event => setAction({ ...action, destination: event.target.value })}
                  >
                    {allowedDestinations.map(item => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                </label>
              ) : (
                <p>To: None</p>
              )}
              {destinations.isError && <p role="alert">Unable to load destination series.</p>}
              {destinations.hasNextPage && (
                <Button
                  variant="outline"
                  disabled={destinations.isFetchingNextPage}
                  onClick={() => void destinations.fetchNextPage()}
                >
                  Load more destination series
                </Button>
              )}
              {action.show.seriesMembership?.series && (
                <p className="text-sm">
                  This permanently removes this show's saved series class-alignment decisions and
                  withdraws any active series publication. Shared identities remain in the original
                  series. A moved show must be aligned again in its destination series.
                </p>
              )}
              {error && <p role="alert">{error}</p>}
              {membershipConflict && (
                <div className="space-y-2" role="alert">
                  <p>
                    Membership changed. Your intended destination is{' '}
                    {destinationName ?? 'unavailable'}.
                  </p>
                  {freshShow ? (
                    <>
                      <p>
                        Saved membership: {freshShow.seriesMembership?.series?.name ?? 'None'} ·{' '}
                        {new Date(freshShow.startDate).getUTCFullYear()}
                      </p>
                      <Button
                        variant="outline"
                        disabled={
                          !freshShow.seriesMembership?.canEdit ||
                          freshShow.orgId !== series.organizationId ||
                          busy
                        }
                        onClick={() => {
                          setAction({ ...action, show: freshShow })
                          setMembershipConflict(false)
                          setFreshShow(null)
                          setError('')
                        }}
                      >
                        I have reviewed the saved membership
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="outline"
                      disabled={busy}
                      onClick={() => void reloadMembership()}
                    >
                      Reload saved membership
                    </Button>
                  )}
                </div>
              )}
              <div className="flex gap-3">
                <Button variant="outline" disabled={busy} onClick={() => setAction(null)}>
                  Cancel
                </Button>
                <Button
                  disabled={
                    busy ||
                    membershipConflict ||
                    !actionStillEditable ||
                    action.destination === (action.show.seriesMembership?.series?.id ?? null) ||
                    Boolean(
                      action.destination &&
                      !allowedDestinations.some(item => item.id === action.destination)
                    )
                  }
                  onClick={() => void saveMembership()}
                >
                  Confirm membership change
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <Dialog
        open={blocker.state === 'blocked'}
        onOpenChange={open => {
          if (!open && blocker.state === 'blocked' && !busy) blocker.reset()
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Leave series settings?</DialogTitle>
            <DialogDescription>Your unsaved changes will be discarded.</DialogDescription>
          </DialogHeader>
          <Button disabled={busy} onClick={() => blocker.state === 'blocked' && blocker.reset()}>
            Stay
          </Button>
          <Button
            variant="destructive"
            disabled={busy}
            onClick={() => blocker.state === 'blocked' && blocker.proceed()}
          >
            Discard changes and leave
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  )
}
